import React, { forwardRef } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";

import { updateOrder } from "@redux/slices/kontrolEdge/orderSlice";
import { useDispatch } from "react-redux";
import { AppDispatch } from "@redux/store/store";
import GenericCheckbox from "@ui-components/Checkbox/GenericCheckbox";
import GenericInput from "@ui-components/Input/GenericInput";
import { GenericRadioCard } from "@ui-components/RadioCard/GenericRadioCard";
export interface InternetConfigurationForm {
  connectorName: string;
  connectorTag: string;
  automaticVlan: boolean;
  vlan: string;
  routingSelection: string;
}

export interface InternetConfigurationRef {
  submit: () => Promise<boolean>;
}

const InternetConfiguration = forwardRef<InternetConfigurationRef, { token: string | null }>(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (ref) => {

  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
  } = useForm<InternetConfigurationForm>({
    defaultValues: {
      connectorName: "",
      connectorTag: "",
      automaticVlan: false,
      vlan: "",
      routingSelection: ""
    },
  });
  
  const dispatch = useDispatch<AppDispatch>();
  const automaticVlan = watch("automaticVlan");
  const displayRoutingOptions = [
    {label : 'Static', value: "static", description: "Get assigned a pblock of public IP addresses to use as static routes", isDisable: false},
    {label : 'Dynamic (private)', value: "dynamicPrivate", description: "Get assigned a private ASN and a block of public IP addresses", isDisable: true},
    {label : 'Dynamic (public)', value: "dynamicPublic", description: "Use your public ASN to advertise your public IP addresses", isDisable: true}
  ];

  const onSubmit: SubmitHandler<InternetConfigurationForm> = (data) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const portData: Partial<any> = {
      basicInfo: {
        name: data.connectorName,
        tag: data.connectorTag,
      },
      configuration: {
        vlan: data.vlan,
        automaticVlan: data.automaticVlan,
      },
    };
    dispatch(updateOrder({ portIndex: 0, portData }));
  };

  // Expose the submit method to the parent
  // useImperativeHandle(ref, () => ({
  //   submit: async () => {
  //     return new Promise<boolean>((resolve) => {
  //       handleSubmit(
  //         (data) => {
  //           onSubmit(data);
  //           resolve(true);
  //         },
  //         () => {
  //           resolve(false);
  //         }
  //       )();
  //     });
  //   },
  // }));

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="px-7 pt-5">
        <h3 className="text-2l font-bold mb-4 text-left text-gray-400">
          Connector Description
        </h3>

        {/* Connector Name Input */}
        <Controller
          name="connectorName"
          control={control}
          rules={{ required: "Connector Name is required" }}
          render={({ field }) => (
            <GenericInput
              label="Connector name"
              placeholder="Enter Connector Name"
              required
              value={field.value ?? ""}
              onChange={field.onChange}
              hasError={!!errors.connectorName}
              helperText={errors.connectorName?.message}
              className="mb-5"
            />
          )}
        />
        {/* Connector Tag Input */}
        <Controller
          name="connectorTag"
          control={control}
          render={({ field }) => (
            <GenericInput
              label="Tag"
              placeholder="#Project Diamond"
              value={field.value ?? ""}
              onChange={field.onChange}
              hasError={!!errors.connectorTag}
              helperText={errors.connectorTag?.message}
            />
          )}
        />
        <div className="mt-5 w-80% border-b border-gray-300" />
      </div>

      <div className="px-7 pt-5">
        <h3 className="text-2l font-bold mb-4 text-left text-gray-400">
          VLAN Configuration
        </h3>

        {/* Automatic VLAN Assigned Checkbox */}
        <Controller
          name="automaticVlan"
          control={control}
          render={({ field }) => (
            <GenericCheckbox
              label="Automatic VLAN assigned"
              checked={field.value}
              onChange={field.onChange}
              className="mb-5"
            />
          )}
        />

        {/* VLAN Range Input */}
        <Controller
          name="vlan"
          control={control}
          rules={{
            required: !automaticVlan ? "VLAN Range is required" : false,
          }}
          render={({ field }) => (
            <GenericInput
              label="Add VLAN"
              placeholder="Range 100-4099"
              value={field.value}
              onChange={field.onChange}
              disabled={automaticVlan}
              required={!automaticVlan}
              hasError={!!errors.vlan}
              helperText={errors.vlan?.message}
            />
          )}
        />
        <div className="mt-5 w-80% border-b border-gray-300" />
      </div>
      {/* Connection Routing  Options */}
      <div className="px-7 pt-5">
          <h3 className="text-2l font-bold mb-4 text-left text-gray-400">
            Dynamic Routing
          </h3>
          <Controller
            name="routingSelection"
            control={control}
            rules={{
              required: "You must select a routing option",
            }}
            
            render={({ field }) => {
              return (
              <div className="space-y-3">
                {displayRoutingOptions.map((option) => (
                  <GenericRadioCard
                    key={option.value}
                    name={field.name}
                    value={option.value}
                    label={option.label}
                    checked={field.value === option.value}
                    onChange={field.onChange}
                    description={option.description}
                    disabled={option.isDisable}
                    className="w-[250px]"
                  />
                ))}
              </div>
              );
            }}
          />
          {errors.routingSelection && (
            <p className="text-red-500 text-sm">{errors.routingSelection.message}</p>
          )}
        </div>
    </form>
  );
});

InternetConfiguration.displayName = "InternetConfiguration";
export default InternetConfiguration;
